/**
 * Comic Generator — fire-and-forget client that triggers server-side generation.
 *
 * The server owns the page generation loop and reads story + config data from
 * Redis. This module just emits `generateComicPrompts` as a trigger — all
 * progress/completion events are handled by persistent listeners in socketStore.ts.
 */

import { useStoryStore } from "../store/storyStore";
import { useComicStore } from "../store/comicStore";
import { useSocketStore } from "../store/socketStore";
import { useErrorStore } from "../store/errorStore";
import { GENERIC_ERROR } from "../store/errorMessages";
import minervaService from "./minervaService";
import { trackComic, trackErrorToast, Comic } from "./metrics";
import { AppEvent } from "./appEvents";
import type { Socket } from "socket.io-client";

/** Timestamp of the most recent generate tap — used by comicHandlers to compute UPL. */
export let generateRequestedAt = 0;

/** Wait until the socket is connected, with a timeout. */
function waitForConnection(socket: Socket, timeoutMs = 10000): Promise<void> {
  if (socket.connected) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      socket.off("connect", onConnect);
      reject(new Error("Timed out waiting for socket connection"));
    }, timeoutMs);
    const onConnect = () => {
      clearTimeout(timer);
      resolve();
    };
    socket.once("connect", onConnect);
  });
}

export async function generateFullComic(): Promise<void> {
  const { beats } = useStoryStore.getState();
  const comicStore = useComicStore.getState();

  if (beats.length === 0) {
    trackErrorToast("Comic", "no_beats");
    useErrorStore.getState().addError("Add some story beats first before building your book!", "warning");
    return;
  }

  const socket = useSocketStore.getState().socket;
  if (!socket) {
    trackErrorToast("Comic", "no_connection");
    useErrorStore.getState().addError(GENERIC_ERROR, "error");
    return;
  }

  comicStore.setPages([]);
  comicStore.setGenState("generating_prompts");

  try {
    generateRequestedAt = Date.now();
    // Wait for a stable connection before emitting.
    await waitForConnection(socket);

    minervaService.recordAppEvent(AppEvent.COMIC_START);
    trackComic(Comic.GenerateRequested);
    socket.emit("generateComicPrompts");
  } catch (err) {
    console.error("[ComicGenerator] Error:", err);
    trackErrorToast("Comic", "connection_timeout");
    useErrorStore.getState().addError(GENERIC_ERROR, "error");
    comicStore.setGenState("idle");
  }
}
